import * as React from 'react';
import { useState } from 'react';
import ReactModal from 'react-modal';
import { useThemeUI, ThemeUIStyleObject } from 'theme-ui';
import srTheme from './theme';
import { Box, Button, CloseIconButton, Text } from './index';
import { useIsMobile } from 'v1/hooks/use-is-media-query';
import './modals.css';

// Needed for accessibility
// http://reactcommunity.org/react-modal/accessibility/
ReactModal.setAppElement('#root');

function baseOverlayStyles(theme: typeof srTheme) {
  return {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: theme.colors.black30,
    zIndex: theme.zIndices.modal,
  };
}
function baseContentStyles(theme: typeof srTheme): ThemeUIStyleObject {
  return {
    position: 'relative',
    outline: 'none',
    top: 'auto',
    right: 'auto',
    bottom: 'auto',
    left: 'auto',
    boxShadow: '0px 8px 80px rgba(0, 0, 0, 0.16)',
    maxWidth: 404,
    width: '100%',
    maxHeight: '90%',
    overflow: 'auto',
    backgroundColor: theme.colors.surfaceGray,
  };
}

type NModalProps = {
  children: React.ReactNode;
  isFullscreen?: boolean;
  OpenButton?: JSX.Element;
  title?: string;
  startOpen?: boolean;
  onClose?: () => void;
  isDark?: boolean;
};

export const NModal = ({
  children,
  isFullscreen,
  OpenButton,
  title,
  startOpen,
  onClose,
  isDark = false,
}: NModalProps) => {
  const { theme } = useThemeUI() as unknown as { theme: typeof srTheme };
  const [isOpen, setIsOpen] = useState(!!startOpen);
  const isMobile = useIsMobile();

  const openModal = () => setIsOpen(true);
  const closeModal = () => {
    if (onClose) onClose();
    setIsOpen(false);
  };

  // Pass closeModal to children so programmatically closing is possible
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { closeModal });
    }
  });

  let regularContentStyles: ThemeUIStyleObject = {
    ...baseContentStyles(theme),
  };

  if (isMobile) {
    regularContentStyles = {
      ...baseContentStyles(theme),
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 'auto',
      right: 0,
      maxHeight: '100%',
      maxWidth: '100%',
      boxShadow: 'none',
    };
  }

  const fullscreenContentStyles = {
    ...baseContentStyles(theme),
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    maxHeight: '100%',
    maxWidth: '100%',
  };

  return (
    <>
      {OpenButton && React.cloneElement(OpenButton, { onClick: openModal })}
      <ReactModal
        isOpen={isOpen}
        onRequestClose={closeModal}
        closeTimeoutMS={250}
        className="primaryModal"
        overlayClassName="primaryModalOverlay"
        style={{
          overlay: {
            ...baseOverlayStyles(theme),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          } as React.CSSProperties,
          content: (isFullscreen
            ? fullscreenContentStyles
            : regularContentStyles) as React.CSSProperties,
        }}
      >
        <Box
          sx={{
            backgroundColor: isDark ? 'surfaceGrayDark' : 'surfaceGray',
            padding: 6,
            minHeight: '100%',
          }}
        >
          {title && (
            <NModalHeader
              closeModal={closeModal}
              title={title}
              isDark={isDark}
            />
          )}
          {childrenWithProps}
        </Box>
      </ReactModal>
    </>
  );
};

function NModalHeader({
  closeModal,
  title,
  isDark = false,
}: {
  title: string;
  closeModal: () => void;
  isDark?: boolean;
}) {
  return (
    <Box variant="flex.center" sx={{ position: 'relative', mb: 6 }}>
      <CloseIconButton
        id="gi-modal-close-button"
        onClick={closeModal}
        sx={{
          position: 'absolute',
          top: '-2px',
          left: 0,
        }}
        color={isDark ? 'white' : 'black'}
      />
      <Text variant={isDark ? 'titleDark' : 'title'}>{title}</Text>
    </Box>
  );
}

type MenuDrawerProps = {
  children: React.ReactNode;
  OpenButton: JSX.Element;
};

export const MenuDrawer = ({ children, OpenButton }: MenuDrawerProps) => {
  const { theme } = useThemeUI() as unknown as { theme: typeof srTheme };
  const [isOpen, setIsOpen] = useState(false);

  const openDrawer = () => setIsOpen(true);
  const closeDrawer = () => setIsOpen(false);

  // Pass closeModal to children so programmatically closing is possible
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { closeDrawer });
    }
  });

  const OpenButtonWithClickHandler = React.cloneElement(OpenButton, {
    onClick: openDrawer,
  });

  return (
    <>
      {OpenButtonWithClickHandler}
      <ReactModal
        isOpen={isOpen}
        onRequestClose={closeDrawer}
        closeTimeoutMS={250}
        className="menuDrawerModal"
        overlayClassName="menuDrawerModalOverlay"
        style={{
          overlay: {
            ...baseOverlayStyles(theme),
            backgroundColor: 'transparent',
            zIndex: theme.zIndices.drawer,
          } as React.CSSProperties,
          content: {
            ...baseContentStyles(theme),
            position: 'absolute',
            top: 0,
            right: 'auto',
            bottom: 0,
            left: 0,
            backgroundColor: '#43454d',
            maxWidth: 375,
            maxHeight: '100%',
            width: '100%',
            boxShadow: 'none',
          } as React.CSSProperties,
        }}
      >
        {childrenWithProps}
      </ReactModal>
    </>
  );
};

type SidebarDrawerProps = {
  children: React.ReactNode;
  CloseButtonContents: JSX.Element;
  isDark?: boolean;
  OpenButtonContents: JSX.Element;
};

export const SidebarDrawer = ({
  children,
  CloseButtonContents,
  isDark,
  OpenButtonContents,
}: SidebarDrawerProps) => {
  const { theme } = useThemeUI() as unknown as { theme: typeof srTheme };
  const [isOpen, setIsOpen] = useState(false);

  const openDrawer = () => setIsOpen(true);
  const closeDrawer = () => setIsOpen(false);

  // Pass closeModal to children so programmatically closing is possible
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { closeDrawer });
    }
  });

  return (
    <>
      {!isOpen && (
        <ButtonPosition>
          <Button onClick={openDrawer} variant="sidebarControl">
            {OpenButtonContents}
          </Button>
        </ButtonPosition>
      )}
      {isOpen && (
        <ButtonPosition isClose={true}>
          <Button onClick={closeDrawer} variant="sidebarControl">
            {CloseButtonContents}
          </Button>
        </ButtonPosition>
      )}
      <ReactModal
        isOpen={isOpen}
        onRequestClose={closeDrawer}
        closeTimeoutMS={250}
        className="sidebarDrawerModal"
        overlayClassName="sidebarDrawerModalOverlay"
        style={{
          overlay: {
            ...baseOverlayStyles(theme),
            zIndex: theme.zIndices.drawer,
          } as React.CSSProperties,
          content: {
            ...baseContentStyles(theme),
            position: 'absolute',
            top: 0,
            right: 'auto',
            bottom: 0,
            left: 0,
            padding: 0,
            backgroundColor: isDark
              ? theme.colors.surfaceGrayDark
              : theme.colors.surfaceGray,
            maxWidth: 320,
            maxHeight: '100%',
            width: '80%',
            boxShadow: 'none',
          } as React.CSSProperties,
        }}
      >
        <Box p={3}>{childrenWithProps}</Box>
      </ReactModal>
    </>
  );
};

function ButtonPosition({
  children,
  isClose,
}: {
  children: React.ReactNode;
  isClose?: boolean;
}) {
  const { theme } = useThemeUI() as unknown as { theme: typeof srTheme };
  return (
    <Box
      sx={{
        position: 'fixed',
        right: 0,
        bottom: 68,
        // This is needed to get the close button on top of the overlay
        // It can't be in the modal because the transform transition breaks the
        // fixed positioning.
        zIndex: isClose ? theme.zIndices.drawer + 1 : theme.zIndices.drawer,
      }}
    >
      {children}
    </Box>
  );
}
